<template>
	<div>
		<div v-if="raceArr.length === 0">
			<!-- make a gray rect -->
			<svg :width="width" height="200px">
				
				<!-- gray background -->
				<rect :width="width" height="200px" fill="gray" fill-opacity=".1"/>
				<g>
					<text
						class='chart-label-desc'
						:x="width / 2"
						y="100px"

						text-anchor="middle"
						dominant-baseline="middle"
					>
						No data available for this district
					</text>
				</g>
			</svg>
		</div>

		<!-- main container -->
		<svg v-if="raceArr.length > 0" :height="height" :width="width" fill="none">
	
			<!-- main container outline -->
			<rect 
				:height="height" 
				:width="width" />

			<!-- race container left -->
			<rect
				:height="height"
				:width="width/2"
				fill="gray"
				fill-opacity=".1"
			/>

			<!-- race container right -->
			<rect
				:transform="'translate(' + width/2 + ', 0)'"
				:height="height"
				:width="width/2"
				fill="black"
				fill-opacity=".1"
			/>


			<!-- legend -->
			<g
				class='axis-label'
				dominant-baseline="hanging"
				x="0"
				y="0"
				:transform="'translate(' + margins[1] + ',' + margins[0] + ')'">
				
				<text text-anchor="start" :aclass='colorClass'>
					<tspan x="0" y='0'>fewer {{ metricText }}</tspan>
					<tspan x="0" :y='legendDy'>than expected</tspan>
				</text>

				<text text-anchor="middle"  :class="{'is-hidden': isMobile}">
					<tspan :x="dims.legend.width / 2" y='0'>equal proportion of</tspan>
					<tspan :x="dims.legend.width / 2" :y='legendDy'>{{ metricText }} and students</tspan>
				</text>

				<text text-anchor="end" :aclass='colorClass'>
					<tspan :x="dims.legend.width" y='0'>more {{ metricText }}</tspan>
					<tspan :x="dims.legend.width" :y='legendDy'>than expected</tspan>
				</text>
			</g>



			<!-- chart -->
			<g :transform="'translate(' + margins[1] + ',' + (margins[0] + legendHeight) + ')'">
				

				<!-- equity line -->
				<line
					:x1="dims.chart.width * 1/2"
					y1="15px"

					:x2="dims.chart.width * 1/2"
					:y2="dims.chart.height"

					stroke="black" 
					stroke-width="1px"
					stroke-dasharray="8 12"
				/>
				<!-- equity line label -->
				<text
					:x="dims.chart.width * 1/2"
					y="0"
					class='axis-label-thick'

					text-anchor="middle"
					dominant-baseline="baseline"
				>
					1:1
				</text>

				<!-- other lines -->
				<g v-for="(tick, i) in axisArr"
					:key="'otherLineKey' + i">

					<!-- text -->
					<text
						:x="scaleX(tick.val)"
						y="0"

						class='axis-label'

						text-anchor="middle"
						dominant-baseline="baseline"
					>
						{{ tick.name }}
					</text>

					<!-- line -->
					<line
						:x1="scaleX(tick.val)"
						y1="15px"

						:x2="scaleX(tick.val)"
						:y2="dims.chart.height"

						stroke="black" 
						stroke-width="1px"
						stroke-opacity=".2"
						stroke-dasharray="8 12"
					/>
				</g>


				<!-- container for each race -->
				<g 
					v-for="(raceObj, i) in raceArr" 
					:key="i"
					:transform="'translate(0, ' + (chartMarginTop + sectionHeight*i + dotOffset) + ')'" 
				>

					<rect :height='sectionHeight' :width='dims.chart.width' />

					<!-- plot a dot -->
					<circle
						:class="colorClass"
						:r="dotRadius"
						:cx="scaleX(raceObj.val)"
						cy="0"
					/>

					<!-- add a label -->
					<g :transform="'translate(0,' + (dotRadius/2 + 2) + ')'">
						<text
							:x="getTextPosition(raceObj.val)"
							:text-anchor="getTextAnchor(raceObj.val)"
						>
							<tspan
								class='chart-label' 
								:x='getTextPosition(raceObj.val)'>{{ raceObj.subgroup }}</tspan>
							<tspan 
								class='chart-sublabel' 
								:class="colorClass" 
								v-if="(raceObj.val <= min) || (raceObj.val >= max)" 
								:x='getTextPosition(raceObj.val)' 
								:y='sublabelDy'>{{ raceObj.display_val }}</tspan>
							<tspan 
								class='chart-label-desc' 
								:x='getTextPosition(raceObj.val)' 
								:y='(raceObj.val <= min) || (raceObj.val >= max) ? sublabelDy + descDy : sublabelDy'>{{ getVal(metricDescArr[0].metric, raceObj.subgroup) }} of {{ metricText }}</tspan>
							<tspan 
								class='chart-label-desc' 
								:x='getTextPosition(raceObj.val)' 
								:y='(raceObj.val <= min) || (raceObj.val >= max) ? sublabelDy + descDy*2 : sublabelDy + descDy'>{{ getVal(metricDescArr[1].metric, raceObj.subgroup) }} of students</tspan>
						</text>
					</g>
				</g>
			</g>
		</svg>

	</div>
</template>


<script>
import { mapGetters } from 'vuex';
import { scaleLinear } from 'd3-scale';

export default {
	props: {
		colorClass: String,
		width: Number,
		metric: String,
		metricDescArr: Array,
	},
	data() {
		return {
			xRange: [-1.8, 1.8],
			min: -0.693147181,
			max: 0.693147181,
			margins: [20, 20, 20, 20],
		}
	},
	computed: {
		...mapGetters(['activeLeaRecords', 'isMobile']),
		metricText() {
			return this.metricDescArr[0].text
		},
		legendHeight() {
			return this.isMobile ? 70 : 80
		},
		chartMarginTop() {
			return this.isMobile ? 30 : 40
		},
		sectionHeight() {
			return this.isMobile ? 100 : 120
		},
		dotRadius() {
			return this.isMobile ? 5 : 7
		},
		dotOffset() {
			return this.isMobile ? 15 : 15
		},
		sublabelDy() {
			return this.isMobile ? 20 : 24
		},
		descDy() {
			return this.isMobile ? 17 : 20
		},
		legendDy() {
			return this.isMobile ? 16 : 20
		},
		raceArr() {
			// filter to just this metric
			const data = this.activeLeaRecords.filter(x => x.metric === this.metric);

			// sort on group
			function sortBySubgroup(a, b) {
				const grpA = a.subgroup;
				const grpB = b.subgroup;

			let comparison = 0;
				if (grpA > grpB) {
					comparison = 1;
				} else if (grpA < grpB) {
					comparison = -1;
				}
				return comparison;
			}
			data.sort(sortBySubgroup)

			return data
		},
		axisArr() {
			let out = [
			]
			if (this.width < 769) {
				out = [
					{'name':'1:5', 'val': -1.609437912},
					{'name': '5:1', 'val': 1.609437912},
					{'name':'1:2', 'val': -0.693147181},
					{'name': '2:1', 'val': 0.693147181},
				]
			} else {
				out = [
					{'name':'1:5', 'val': -1.609437912},
					{'name': '5:1', 'val': 1.609437912},
					{'name':'1:2', 'val': -0.693147181},
					{'name': '2:1', 'val': 0.693147181},
				]
			}
			return out
		},
		height() {
			return this.margins[0] + this.legendHeight + this.chartMarginTop + this.raceArr.length * this.sectionHeight + this.margins[2]
		},
		dims() {
			return {
				chart: {
					'height': this.chartMarginTop + this.sectionHeight * this.raceArr.length,
					'width': this.width - this.margins[1] - this.margins[3]
				},
				legend: {
					'height': this.legendHeight,
					'width': this.width - this.margins[1] - this.margins[3]
				}
			}
		}
	},
	methods: {
		getPosition(val) {
			const xVal = this.scaleX(val);
			const pctVal = xVal / this.dims.chart.width;

			if (this.isMobile) {
				if (pctVal > .5) {
					return 'left'
				} else {
					return 'right'
				}

			} else {
				if (pctVal > .67) {
					return 'left'
				} else if (pctVal > .5) {
					return 'right'
				} else if (pctVal > .33) {
					return 'left'
				} else {
					return 'right'
				}
			}
		},
		getTextPosition(val) {
			const pos = this.getPosition(val);

			let xVal = this.scaleX(val);
			let buffer = 0;
			if (pos === 'right') {
				buffer = this.isMobile ? 15 : 20;
			} else {
				buffer = this.isMobile ? -15 : -20;
			}
			return xVal + buffer;
		},
		getTextAnchor(val) {
			const pos = this.getPosition(val);
			let out = '';
			if ( pos === 'right' ) {
				out = 'start';
			} else {
				out = 'end';
			}
			return out;
		},
		scaleX(val) {
			var scale = scaleLinear()
				.domain(this.xRange)
				.range([0, this.dims.chart.width]);

			let myVal = val;
			if (myVal < this.xRange[0]) {
				myVal = this.xRange[0];
			} else if (myVal > this.xRange[1]) {
				myVal = this.xRange[1];
			}
			return scale(myVal)
		},
		getVal(metric, subgroup) {
			// filter to just this metric
			const data = this.activeLeaRecords.filter(x => (x.metric === metric) && (x.subgroup === subgroup));

			if (data.length > 0) {
				return data[0].display_val;
			} else {
				return ''
			}
		}
	}
}
</script>